import { useCallback } from 'react';
import { Logger } from '@saviynt/common';

import { downloadBlobAsFile } from '../../../shared/utils';
import { useGenerateSyncExportMutation } from '../data-layer/entReportsApi';
import { type CreateSyncExportRequest } from '../types';

/**
 * useEntSyncExport Hook
 *
 * Custom hook for managing synchronous entitlement report exports with immediate
 * file download functionality. Handles direct export generation and automatic
 * file download without background processing or polling.
 *
 * @hook
 * @returns {Object} Sync export utilities including:
 *   - exportSyncReport: Function to generate and download report immediately
 *   - isExporting: Boolean indicating if sync export is in progress
 *
 * @example
 * const { exportSyncReport, isExporting } = useEntSyncExport();
 *
 * // Generate and download report synchronously
 * await exportSyncReport({
 *   reportType: 'INHERENT_ENTITLEMENT_RISK',
 *   format: 'EXCEL',
 *   filters: {...},
 *   columnConfig: {...}
 * });
 */
const useEntSyncExport = () => {
  const [generateSyncExport, { isLoading }] = useGenerateSyncExportMutation();

  const exportSyncReport = useCallback(
    async (payload: CreateSyncExportRequest) => {
      try {
        const blob = (await generateSyncExport(payload).unwrap()) as Blob;

        const fileName = `Sod_Entitlement_Report_${new Date().getFullYear()}.xlsx`;

        downloadBlobAsFile(blob, fileName);
      } catch (error) {
        Logger.log('error', 'Sync export failed:', error);
        throw error;
      }
    },
    [generateSyncExport]
  );

  return {
    exportSyncReport,
    isExporting: isLoading,
  };
};

export default useEntSyncExport;
